import { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import BackToTop from "@/components/feature/BackToTop";
import ScrollToTop from "@/components/feature/ScrollToTop";
import Seo from "@/components/feature/Seo";
import { faqs, faqStructuredData } from "@/mocks/faqs";

const formAddr = "https://readdy.ai/api/form/da1ejsomlpfrg29fpgr0";

export default function Faq() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [status, setStatus] = useState<"idle" | "ok" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);

    const honeypot = data.get("phone_alt");
    if (honeypot && String(honeypot).trim() !== "") {
      setStatus("ok");
      setFormError("");
      form.reset();
      setName("");
      setEmail("");
      setMessage("");
      return;
    }
    data.delete("phone_alt");

    try {
      const res = await fetch(formAddr, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(data as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string } } = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        /* ignore */
      }
      const serverMsg = parsed?.meta?.message || parsed?.meta?.detail || responseText;
      const isSpam = /spam/i.test(responseText + (serverMsg || ""));
      const success = res.ok && parsed?.code === "OK" && !isSpam;

      if (success) {
        setStatus("ok");
        setFormError("");
        form.reset();
        setName("");
        setEmail("");
        setMessage("");
      } else {
        setStatus("error");
        setFormError(
          serverMsg && typeof serverMsg === "string" && serverMsg.trim()
            ? serverMsg
            : "提交失败，请稍后再试。",
        );
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，提交未成功，请稍后重试。");
    }
  };

  return (
    <>
      <ScrollToTop />
      <Seo
        title="常见问题 - 火影同人志阅读与投稿答疑"
        description="火影同人志常见问题解答，涵盖同人作品分类方式、免费阅读、更新频率、投稿流程与版权说明等，帮助读者和作者快速了解平台使用方式。"
        keywords="火影同人志,常见问题,同人投稿,同人阅读,版权说明"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faqStructuredData,
          },
        ]}
      />

      <Navbar />

      <section className="bg-secondary-700">
        <div className="mx-auto w-full max-w-7xl px-4 py-12 md:px-6 md:py-16">
          <nav aria-label="面包屑" className="flex items-center gap-1.5 text-xs text-secondary-200">
            <Link to="/" className="hover:text-primary-300">
              首页
            </Link>
            <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
            <span>常见问题</span>
          </nav>
          <h1 className="mt-4 font-heading text-2xl font-black text-background-50 md:text-4xl">常见问题</h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-secondary-100 md:text-base">
            关于同人作品分类、阅读方式、投稿流程与版权问题，这里集中为你解答。
          </p>
        </div>
      </section>

      <section className="mx-auto w-full max-w-7xl px-4 py-12 md:px-6 md:py-16">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-5 lg:gap-12">
          {/* FAQ list */}
          <div className="space-y-3 lg:col-span-3">
            {faqs.map((f, i) => {
              const open = openIndex === i;
              return (
                <div key={f.q} className={`rounded-lg border ${open ? "border-primary-300 bg-primary-50" : "border-background-200 bg-background-100"}`}>
                  <button
                    type="button"
                    onClick={() => setOpenIndex(open ? null : i)}
                    className="flex w-full items-center justify-between gap-3 px-4 py-4 text-left md:px-5"
                    aria-expanded={open}
                  >
                    <span className="text-sm font-bold text-foreground-950 md:text-base">{f.q}</span>
                    <span className={`flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full transition-all ${open ? "rotate-45 bg-primary-500 text-background-50" : "bg-primary-500/10 text-primary-700"}`}>
                      <i className="ri-add-line" aria-hidden="true"></i>
                    </span>
                  </button>
                  {open && (
                    <p className="px-4 pb-4 text-sm leading-relaxed text-foreground-700 md:px-5 md:text-base">
                      {f.a}
                    </p>
                  )}
                </div>
              );
            })}
          </div>

          {/* Contact form */}
          <aside className="lg:col-span-2">
            <div className="rounded-lg bg-background-100 p-6 md:p-7">
              <span className="text-xs font-bold uppercase tracking-widest text-accent-500">没有找到答案？</span>
              <h2 className="mt-2 font-heading text-xl font-black text-foreground-950 md:text-2xl">
                给我们留言
              </h2>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                留下你的问题，我们会尽快回复你。带 * 为必填项。
              </p>

              <form onSubmit={handleSubmit} data-readdy-form="问题反馈与咨询" className="mt-5 space-y-4">
                <div>
                  <label htmlFor="faq-name" className="mb-1.5 block text-sm font-medium text-foreground-800">
                    你的称呼 *
                  </label>
                  <input
                    id="faq-name"
                    name="name"
                    type="text"
                    required
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="怎么称呼你"
                    className="w-full rounded-md border border-background-200 bg-background-50 px-3 py-2.5 text-sm text-foreground-950 placeholder:text-foreground-500 focus:border-primary-400 focus:outline-none"
                  />
                </div>
                <div>
                  <label htmlFor="faq-email" className="mb-1.5 block text-sm font-medium text-foreground-800">
                    邮箱 *
                  </label>
                  <input
                    id="faq-email"
                    name="email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="用于接收回复"
                    className="w-full rounded-md border border-background-200 bg-background-50 px-3 py-2.5 text-sm text-foreground-950 placeholder:text-foreground-500 focus:border-primary-400 focus:outline-none"
                  />
                </div>
                <div>
                  <label htmlFor="faq-message" className="mb-1.5 block text-sm font-medium text-foreground-800">
                    你的问题 *
                  </label>
                  <textarea
                    id="faq-message"
                    name="message"
                    required
                    maxLength={500}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    rows={5}
                    placeholder="请描述你的问题（最多 500 字）"
                    className="w-full resize-none rounded-md border border-background-200 bg-background-50 px-3 py-2.5 text-sm text-foreground-950 placeholder:text-foreground-500 focus:border-primary-400 focus:outline-none"
                  />
                  <p className="mt-1 text-right text-xs text-foreground-500">{message.length}/500</p>
                </div>
                {/* Honeypot (anti-spam) */}
                <input
                  type="text"
                  name="phone_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="hp-field"
                />
                <button
                  type="submit"
                  className="inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-bold text-background-50 transition-colors hover:bg-primary-600"
                >
                  提交问题 <i className="ri-send-plane-fill" aria-hidden="true"></i>
                </button>
              </form>

              {status === "ok" && (
                <p className="mt-4 rounded-md bg-primary-500/10 px-4 py-3 text-sm text-primary-700">
                  提交成功，我们会尽快回复你，感谢你的关注！
                </p>
              )}
              {status === "error" && (
                <p className="mt-4 rounded-md bg-accent-500/10 px-4 py-3 text-sm text-accent-700">
                  {formError || "提交失败，请稍后再试。"}
                </p>
              )}
            </div>
          </aside>
        </div>
      </section>

      <Footer />
      <BackToTop />
    </>
  );
}